import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listInventoryLocations, qk } from "@/lib/queries";
import { useT } from "@/lib/i18n";

export function Inventory() {
  const t = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: qk.inventoryLocations(),
    queryFn: listInventoryLocations,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-semibold">
          {t("inventory.title", "Inventory")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "inventory.subtitle",
            "Locations + per-location stock. Editor + transfers ship in a follow-up."
          )}
        </p>
      </div>

      <h2 className="font-display text-sm font-semibold text-muted-foreground">
        {t("inventory.locations", "Locations")}
      </h2>

      {error && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="pt-6 text-sm text-destructive">
            {t("inventory.errorLoading", "Failed to load")}: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("inventory.colCode", "Code")}</TableHead>
              <TableHead>{t("inventory.colName", "Name")}</TableHead>
              <TableHead>{t("inventory.colType", "Type")}</TableHead>
              <TableHead>{t("inventory.colPickup", "Pickup point")}</TableHead>
              <TableHead>{t("inventory.colStatus", "Status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  {t("common.loading", "Loading…")}
                </TableCell>
              </TableRow>
            )}
            {data?.locations.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-mono text-xs">{l.code}</TableCell>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell>
                  {l.type === "shop"
                    ? t("inventory.typeShop", "shop")
                    : t("inventory.typeWarehouse", "warehouse")}
                </TableCell>
                <TableCell>
                  {l.isPickupPoint ? (
                    <Badge variant="outline">{t("inventory.pickupYes", "pickup")}</Badge>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={l.active ? "default" : "secondary"}>
                    {l.active
                      ? t("inventory.statusActive", "active")
                      : t("inventory.statusInactive", "inactive")}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <p className="text-xs text-muted-foreground">
        {t(
          "inventory.note",
          "Per-product stock editor lives on the product detail page. Movement-history view + transfer UI land in a follow-up."
        )}
      </p>
    </div>
  );
}
